// ── 세무사 vs 시스템 급여 비교 모달 ────────────────────────────────────────────
// PayrollUploadSection에서 "비교" 클릭 시 표시
// 직원별 접이식 행 → 펼치면 항목별 (시스템 | 세무사 | 차이) 세로 비교

import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalHeaderSub, stdModalCloseBtn } from '../adminlayout/designStandard';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';

// ── 차이 색상 (양수=빨강, 음수=초록, 0=회색) ────────────────────────────────
const diffColor = (d) => d > 0 ? '#dc2626' : d < 0 ? '#16a34a' : 'var(--text-muted)';
const diffText = (d) => d !== 0 ? (d > 0 ? '+' : '') + fmt(d) : '-';
const needsPayrollOfficeItemReview = (item) =>
  item?.type === 'earning' && !item.isSummary && Number(item.diff) !== 0 && !item.canApply;
const needsPayrollOfficeReview = (comp) => {
  const items = comp?.items || [];
  if (items.some((it) => needsPayrollOfficeItemReview(it))) return true;
  const hasEarningSummaryDiff = items.some((it) => it.type === 'earning' && it.isSummary && Number(it.diff) !== 0);
  const hasApplicableEarningDiff = items.some((it) => it.type === 'earning' && Number(it.diff) !== 0 && it.canApply);
  return hasEarningSummaryDiff && !hasApplicableEarningDiff;
};

// ── 스타일 ──────────────────────────────────────────────────────────────────
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalStyle = {
  background: 'var(--bg-card)', borderRadius: 14, padding: '24px 28px',
  maxWidth: 680, width: 'min(680px, calc(100vw - 24px))',
  maxHeight: 'min(85vh, calc(100dvh - 24px))', overflow: 'auto',
  boxSizing: 'border-box',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
};
const btnApply = {
  border: 'none', borderRadius: 8, padding: '8px 22px',
  background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff',
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const btnClose = {
  border: '1.5px solid var(--border)', borderRadius: 8, padding: '7px 18px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};

// ── 직원 요약 행 (클릭 → 항목 상세 펼침) ────────────────────────────────────
function EmployeeRow({ comp, isOpen, onToggle }) {
  const items = comp.items || [];
  const compNeedsOfficeReview = needsPayrollOfficeReview(comp);
  const hasItemOfficeReview = items.some((it) => needsPayrollOfficeItemReview(it));
  const sysTotal = Number(comp.sysTotal?.net) || 0;
  const uploadTotal = comp.uploadTotal?.net == null ? sysTotal : Number(comp.uploadTotal.net) || 0;
  const totalDiff = comp.uploadTotal?.net == null ? 0 : uploadTotal - sysTotal;
  const hasDiff = totalDiff !== 0;

  return (
    <>
      {/* ── 요약 행: 직원명 + 차인지급액 비교 ── */}
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', background: hasDiff ? 'rgba(220,38,38,0.04)' : 'transparent' }}
      >
        <td style={{ padding: '10px 12px', fontWeight: 800, fontSize: 13, color: 'var(--text)', borderBottom: isOpen ? 'none' : '1px solid var(--border)' }}>
          <span style={{ display: 'inline-block', width: 16, fontSize: 10, color: 'var(--text-muted)' }}>
            {isOpen ? '▼' : '▶'}
          </span>
          {comp.name}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, color: 'var(--text-muted)', borderBottom: isOpen ? 'none' : '1px solid var(--border)' }}>
          {fmt(sysTotal)}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: hasDiff ? 700 : 400, color: hasDiff ? '#2563eb' : 'var(--text)', borderBottom: isOpen ? 'none' : '1px solid var(--border)' }}>
          {fmt(uploadTotal)}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: diffColor(totalDiff), borderBottom: isOpen ? 'none' : '1px solid var(--border)' }}>
          {diffText(totalDiff)}
        </td>
      </tr>

      {/* ── 상세 행: 지급/공제 항목별 비교 (펼침 시) ── */}
      {isOpen && items.map((it, i) => {
        const d = it.diff;
        const isLast = i === items.length - 1;
        const officeReview = needsPayrollOfficeItemReview(it)
          || (it.isSummary && compNeedsOfficeReview && !hasItemOfficeReview);
        return (
          <tr key={i} style={{ background: d !== 0 ? 'rgba(37,99,235,0.04)' : 'transparent' }}>
            <td style={{ padding: '5px 12px 5px 40px', fontSize: 12, color: 'var(--text-sub)', borderBottom: isLast ? '1px solid var(--border)' : 'none' }}>
              <span style={{ display: 'inline-block', minWidth: 34, marginRight: 6, fontSize: 10, fontWeight: 800, color: it.type === 'earning' ? '#2563eb' : '#9f423f' }}>
                {it.type === 'earning' ? '지급' : '공제'}
              </span>
              {it.name}
              {it.diff !== 0 && !it.canApply && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#d97706', fontWeight: 800 }}>
                  {officeReview ? '세무사 수정 요청' : '확인필요'}
                </span>
              )}
            </td>
            <td style={{ padding: '5px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', borderBottom: isLast ? '1px solid var(--border)' : 'none' }}>
              {fmt(it.systemValue)}
            </td>
            <td style={{ padding: '5px 12px', textAlign: 'right', fontSize: 12, fontWeight: d !== 0 ? 700 : 400, color: d !== 0 ? '#2563eb' : 'var(--text)', borderBottom: isLast ? '1px solid var(--border)' : 'none' }}>
              {fmt(it.uploadValue)}
            </td>
            <td style={{ padding: '5px 12px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: diffColor(d), borderBottom: isLast ? '1px solid var(--border)' : 'none' }}>
              {diffText(d)}
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default function PayrollCompareModal({ batchId, periodId, isConfirmed = false, showToast, onClose, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  // 펼쳐진 직원 set (차이 있는 직원은 자동 펼침)
  const [openSet, setOpenSet] = useState(new Set());

  // ── 비교 데이터 로드 ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiClient.get(
        `/api/admin/payroll/upload/batches/${batchId}/compare?period_id=${periodId}`,
      );
      if (res.ok && res.data?.data) {
        setData(res.data.data);
        // 차이 있는 직원 자동 펼침
        const autoOpen = new Set();
        (res.data.data.comparisons || []).forEach((c) => {
          if (c.matched && (c.netDiff !== 0 || c.items?.some((it) => it.diff !== 0))) {
            autoOpen.add(c.name);
          }
        });
        setOpenSet(autoOpen);
      } else {
        showToast(res.data?.message || '비교 데이터 로드 실패', 'error');
      }
      setLoading(false);
    })();
  }, [batchId, periodId, showToast]);

  // ── 적용 ──────────────────────────────────────────────────────────────────
  const handleApply = async () => {
    if (isConfirmed) {
      showToast('확정된 급여월은 세무사값을 적용할 수 없습니다. 확정 취소 후 진행하세요.', 'error');
      return;
    }
    if (!window.confirm('세무사 값으로 공제 항목과 월별 지급항목(미사용연차수당)을 반영하시겠습니까?\n기본급/인센티브처럼 별도 검토가 필요한 지급항목은 자동으로 덮어쓰지 않습니다.')) return;
    setApplying(true);
    const res = await apiClient.post('/api/admin/payroll/upload/apply', {
      batch_id: batchId, period_id: periodId,
    });
    setApplying(false);
    if (res.ok && res.data?.success) {
      showToast(`${res.data.appliedCount}명의 세무사 값이 적용되었습니다.`, 'success');
      if (onApplied) onApplied();
      onClose();
    } else {
      showToast(res.data?.message || '적용 실패', 'error');
    }
  };

  // ── 토글 ──────────────────────────────────────────────────────────────────
  const toggle = (name) => setOpenSet((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const comparisons = useMemo(() => data?.comparisons || [], [data]);
  const unhandledColumns = data?.unhandledColumns || [];
  const matched = useMemo(() => comparisons.filter((c) => c.matched), [comparisons]);
  const unmatched = useMemo(() => comparisons.filter((c) => !c.matched), [comparisons]);
  const diffCount = useMemo(() => matched.filter((c) =>
    c.netDiff !== 0 || c.items?.some((it) => it.diff !== 0),
  ).length, [matched]);
  const officeReviewNames = useMemo(() => matched
    .filter((c) => needsPayrollOfficeReview(c))
    .map((c) => c.name), [matched]);

  return (
    <div style={overlay} {...overlayCloseProps}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* ── 헤더 — 검정 표준 밴드 (카드 패딩 상쇄용 음수 마진) ── */}
        <div style={{ ...stdModalHeaderBar(14), margin: '-24px -28px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>🔍</div>
            <div style={{ minWidth: 0 }}>
              <div style={stdModalHeaderTitle}>세무사 vs 시스템 비교</div>
              <div style={stdModalHeaderSub}>
                매칭 {matched.length}명 · 미매칭 {unmatched.length}명 · 차이 발생 {diffCount}명
              </div>
            </div>
          </div>
          <button style={stdModalCloseBtn} onClick={onClose}>✕</button>
        </div>
        {/* ── 액션 버튼 (기존 그대로) ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', margin: '16px 0 18px' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{
                ...btnApply,
                opacity: isConfirmed || applying || matched.length === 0 ? 0.55 : 1,
                cursor: isConfirmed || applying || matched.length === 0 ? 'not-allowed' : 'pointer',
              }}
              onClick={handleApply}
              disabled={isConfirmed || applying || matched.length === 0}
              title={isConfirmed ? '확정된 급여월은 확정 취소 후 적용할 수 있습니다.' : undefined}
            >
              {applying ? '적용 중...' : '세무사 값 적용'}
            </button>
            <button style={btnClose} onClick={onClose}>닫기</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>비교 중...</div>
        ) : (
          <>
            {isConfirmed && (
              <div style={{ padding: '10px 14px', background: 'rgba(220,38,38,0.06)', borderRadius: 8, border: '1px solid rgba(220,38,38,0.18)', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>
                  확정된 급여월입니다
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                  비교는 가능하지만 세무사값 적용은 할 수 없습니다. 필요한 경우 확정 취소 후 진행하세요.
                </div>
              </div>
            )}
            {officeReviewNames.length > 0 && (
              <div style={{ padding: '10px 14px', background: 'rgba(217,119,6,0.08)', borderRadius: 8, border: '1px solid rgba(217,119,6,0.22)', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', marginBottom: 4 }}>
                  세무사 수정 요청 필요
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-sub)', lineHeight: 1.5 }}>
                  기본급, 식대 같은 일반 지급항목 차이는 시스템에서 자동 적용하지 않습니다.
                  시스템 금액이 맞다면 세무사랑 파일 수정을 세무사에게 요청하세요.
                  {' '}대상: {officeReviewNames.join(', ')}
                </div>
              </div>
            )}
            {unhandledColumns.length > 0 && (
              <div style={{ padding: '10px 14px', background: 'rgba(217,119,6,0.08)', borderRadius: 8, border: '1px solid rgba(217,119,6,0.22)', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', marginBottom: 5 }}>
                  미처리 컬럼 확인 필요
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--text-sub)' }}>
                  {unhandledColumns.map((col) => (
                    <div key={col.header}>
                      {col.header} · {col.reason} · 값 있는 직원 {col.nonEmptyCount}명
                      {Number(col.totalAmount) !== 0 ? ` · 합계 ${fmt(col.totalAmount)}원` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* ── 비교 테이블 (직원별 접이식) ── */}
            {matched.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                      직원명
                    </th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', textAlign: 'right', width: 120 }}>
                      시스템 차인지급액
                    </th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#2563eb', borderBottom: '2px solid var(--border)', textAlign: 'right', width: 120 }}>
                      세무사 차인지급액
                    </th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', textAlign: 'right', width: 100 }}>
                      차이
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map((comp) => (
                    <EmployeeRow
                      key={comp.name}
                      comp={comp}
                      isOpen={openSet.has(comp.name)}
                      onToggle={() => toggle(comp.name)}
                    />
                  ))}
                </tbody>
              </table>
            )}

            {/* ── 미매칭 직원 ── */}
            {unmatched.length > 0 && (
              <div style={{ padding: '10px 14px', background: 'rgba(220,38,38,0.05)', borderRadius: 8, border: '1px solid rgba(220,38,38,0.15)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>
                  미매칭 직원 ({unmatched.length}명)
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                  {unmatched.map((u) => u.name).join(', ')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  시스템에 등록되지 않은 직원입니다. 직원 관리에서 등록 후 다시 비교하세요.
                </div>
              </div>
            )}

            {matched.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                매칭된 직원이 없습니다. 급여 계산을 먼저 실행하세요.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
